/**
 * The served-market chrome of the institutional site (site completion; design v2 `Site Institucional v2.dc.html`): the
 * glass top nav with its dropdowns — and a menu on narrow screens —,
 * the ink footer, and the one link primitive every page uses. A pack names each destination with a neutral `SiteTarget`
 * token (`@vello/web-entry`); `resolveSiteTarget` (`../src/site-links.ts`) decides what it is and {@link SiteLinkTo} makes
 * it a real link — an in-site page is a client navigation, the Central chat opens the widget when the site carries it,
 * a legal text or an outside address is an ordinary link. A token the site cannot resolve renders as plain text, never
 * as a dead click.
 *
 * Neutral by construction: every string comes from the injected `SiteContent` or the chrome catalog, every colour is a
 * `@vello/tokens` variable (the visual gate enforces it).
 */
import { createContext, type MouseEvent, type ReactElement, type ReactNode, useContext, useEffect, useId, useRef, useState } from 'react';
import wordmarkInk from '@vello/brand/assets/wordmark/svg/vello-wordmark-ink.svg';
import wordmarkWhite from '@vello/brand/assets/wordmark/svg/vello-wordmark-branco.svg';
import type { SiteContent, SiteNavMenu, SitePages, SiteTarget } from '@vello/web-entry';
import { resolveSiteTarget, type ResolvedSiteTarget } from '../src/site-links.js';
import type { SitePage } from '../src/site-routes.js';
import type { Copy } from './i18n';
import { Icon } from './icons';

/** Everything a page needs to link: the resolved market, the copy, and how to go somewhere. */
export interface SiteNav {
  readonly page: SitePage;
  readonly content: SiteContent;
  readonly t: Copy;
  readonly locale: string;
  /** The address of a destination, or undefined when it does not exist for this market. */
  readonly hrefOf: (target: ResolvedSiteTarget) => string | undefined;
  /** A click on a resolved destination (in-site navigation, the chat, or the browser's own behaviour). */
  readonly go: (event: MouseEvent<HTMLAnchorElement>, target: ResolvedSiteTarget) => void;
  /** Whether the site carries the Central chat widget. */
  readonly widgetEnabled: boolean;
  /** The resolved market (ISO 3166-1 alpha-2) and the api origin the live pages read (careers openings). */
  readonly country: string;
  readonly apiBaseUrl: string;
}

const SiteNavContext = createContext<SiteNav | undefined>(undefined);

export const SiteNavProvider = SiteNavContext.Provider;

export function useSiteNav(): SiteNav {
  const nav = useContext(SiteNavContext);
  if (nav === undefined) throw new Error('Vello site: SiteNavProvider is missing');
  return nav;
}

/** The pack key of each content page (the pages the market may leave out). */
export const PAGE_CONTENT_KEY: Readonly<Partial<Record<SitePage, keyof SitePages>>> = {
  drivers: 'drivers',
  business: 'business',
  safety: 'safety',
  help: 'help',
  about: 'about',
  press: 'press',
  careers: 'careers',
  'sign-in': 'signIn',
  download: 'download',
};

/** A link to a pack destination; plain text when the destination does not resolve. */
export function SiteLinkTo({
  target,
  className,
  children,
  current,
}: {
  target: SiteTarget;
  className?: string;
  children: ReactNode;
  /** Mark the link as the current page (`aria-current="page"`). */
  current?: boolean;
}): ReactElement {
  const nav = useSiteNav();
  const resolved = resolveSiteTarget(target);
  const href = nav.hrefOf(resolved);
  if (href === undefined) return <span className={className}>{children}</span>;
  return (
    <a className={className} href={href} onClick={(e) => nav.go(e, resolved)} {...(current === true ? { 'aria-current': 'page' as const } : {})}>
      {children}
    </a>
  );
}

/** One nav dropdown panel (design v2 mega menu): a titled grid of entries and the ink feature card. */
function NavMenuPanel({ menu, id, shown }: { menu: SiteNavMenu; id: string; shown: boolean }): ReactElement {
  return (
    <div id={id} className={shown ? 'nav-mega is-in' : 'nav-mega'}>
      <div className="nav-mega-inner">
        <div className="nav-mega-main">
          <span className="eyebrow">{menu.title}</span>
          <div className="nav-mega-grid">
            {menu.items.map((it) => (
              <SiteLinkTo key={`${it.title}-${it.target}`} target={it.target} className="nav-mega-item">
                <span className="nav-mega-icon"><Icon name={it.icon} size={20} /></span>
                <span className="nav-mega-copy">
                  <span className="nav-mega-title">{it.title}</span>
                  <span className="nav-mega-desc">{it.desc}</span>
                </span>
              </SiteLinkTo>
            ))}
          </div>
        </div>
        <SiteLinkTo target={menu.feature.target} className="nav-mega-feature">
          <span className="nav-mega-feature-kicker">{menu.feature.kicker}</span>
          <span className="nav-mega-feature-title">{menu.feature.title}</span>
          <span className="nav-mega-feature-desc">{menu.feature.desc}</span>
        </SiteLinkTo>
      </div>
    </div>
  );
}

/**
 * The sticky glass top nav (design v2): wordmark; the market's sections — an item with a `menu` opens its dropdown on
 * hover or click (a sliding highlight follows the pointer), the others are links —; help, sign in and the download
 * action; a shadow once the page scrolls; a menu button below 900 px.
 */
function SiteHeader(): ReactElement {
  const { content: c, page, t } = useSiteNav();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(-1);
  const [scrolled, setScrolled] = useState(false);
  const menuId = useId();
  const megaId = useId();
  const navWrap = useRef<HTMLDivElement>(null);
  const pill = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Every navigation closes the menus.
  useEffect(() => {
    setOpen(false);
    setMenu(-1);
  }, [page]);
  useEffect(() => {
    if (!open && menu < 0) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        setMenu(-1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, menu]);
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /** Slide the highlight under a nav entry (custom properties, so no inline style), or hide it. */
  const movePill = (el: HTMLElement | null): void => {
    const p = pill.current;
    if (p === null) return;
    if (el === null) {
      p.classList.remove('is-on');
      return;
    }
    p.style.setProperty('--pill-left', `${el.offsetLeft}px`);
    p.style.setProperty('--pill-width', `${el.offsetWidth}px`);
    p.classList.add('is-on');
  };
  const enter = (i: number, el: HTMLElement): void => {
    clearTimeout(closeTimer.current);
    movePill(el);
    setMenu(c.nav[i]?.menu !== undefined ? i : -1);
  };
  const leave = (): void => {
    movePill(null);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenu(-1), 200);
  };

  const menuLabel = c.navMenu ?? t('site.nav.menu');
  const current = (key: string): boolean => key === page;
  const active = menu >= 0 ? c.nav[menu]?.menu : undefined;
  const headerClass = ['nav', scrolled ? 'nav--scrolled' : '', menu >= 0 ? 'nav--menu' : ''].filter((x) => x !== '').join(' ');
  return (
    <header className={headerClass} onMouseLeave={leave}>
      <div className="nav-inner">
        <SiteLinkTo target="home" className="nav-home">
          <img className="nav-wordmark" src={wordmarkInk} alt="Vello" />
        </SiteLinkTo>
        <nav className="nav-links" aria-label="Vello">
          <div className="nav-wrap" ref={navWrap}>
            <span className="nav-pill" ref={pill} aria-hidden="true" />
            {c.nav.map((item, i) =>
              item.menu !== undefined ? (
                <button
                  key={item.key}
                  type="button"
                  className={current(item.key) ? 'nav-link nav-trigger nav-link--active' : 'nav-link nav-trigger'}
                  aria-expanded={menu === i}
                  aria-controls={megaId}
                  onMouseEnter={(e) => enter(i, e.currentTarget)}
                  onFocus={(e) => movePill(e.currentTarget)}
                  onClick={(e) => {
                    movePill(e.currentTarget);
                    setMenu((m) => (m === i ? -1 : i));
                  }}
                >
                  {item.label}
                  <span className={menu === i ? 'nav-chevron is-open' : 'nav-chevron'}><Icon name="chevron-down" size={14} /></span>
                </button>
              ) : (
                <span key={item.key} className="nav-link-slot" onMouseEnter={(e) => enter(i, e.currentTarget)}>
                  <SiteLinkTo target={item.key} className={current(item.key) ? 'nav-link nav-link--active' : 'nav-link'} current={current(item.key)}>
                    {item.label}
                  </SiteLinkTo>
                </span>
              ),
            )}
          </div>
        </nav>
        <span className="nav-spacer" />
        <div className="nav-actions">
          {c.navHelp !== undefined && (
            <SiteLinkTo target="help" className="btn btn--quiet" current={page === 'help'}>
              {c.navHelp}
            </SiteLinkTo>
          )}
          <SiteLinkTo target="sign-in" className="btn btn--ghost">{c.navSignIn}</SiteLinkTo>
          <SiteLinkTo target="download" className="btn btn--primary">{c.navDownload}</SiteLinkTo>
        </div>
        <button
          type="button"
          className="nav-menu"
          aria-expanded={open}
          aria-controls={menuId}
          aria-label={open ? t('site.nav.closeMenu') : menuLabel}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name={open ? 'x' : 'menu'} size={22} />
        </button>
      </div>
      {active !== undefined ? <NavMenuPanel menu={active} id={megaId} shown /> : <div id={megaId} hidden />}
      <div id={menuId} className="nav-panel" hidden={!open}>
        <nav className="nav-panel-links" aria-label={menuLabel}>
          {c.nav.map((item) =>
            item.menu !== undefined ? (
              <div key={item.key} className="nav-panel-group">
                <span className="eyebrow">{item.menu.title}</span>
                {item.menu.items.map((it) => (
                  <SiteLinkTo key={`${it.title}-${it.target}`} target={it.target} className="nav-panel-link">
                    {it.title}
                  </SiteLinkTo>
                ))}
              </div>
            ) : (
              <SiteLinkTo key={item.key} target={item.key} className="nav-panel-link" current={current(item.key)}>
                {item.label}
              </SiteLinkTo>
            ),
          )}
          {c.navHelp !== undefined && (
            <SiteLinkTo target="help" className="nav-panel-link" current={page === 'help'}>
              {c.navHelp}
            </SiteLinkTo>
          )}
        </nav>
        <div className="nav-panel-actions">
          <SiteLinkTo target="sign-in" className="btn btn--ghost btn--lg">{c.navSignIn}</SiteLinkTo>
          <SiteLinkTo target="download" className="btn btn--primary btn--lg">{c.navDownload}</SiteLinkTo>
        </div>
      </div>
    </header>
  );
}

/** The ink footer: brand, the market's link columns, then copyright, account deletion and region. */
function SiteFooter({ deletion }: { deletion: { readonly href: string; readonly label: string; readonly onNavigate: (e: MouseEvent<HTMLAnchorElement>, href: string) => void } }): ReactElement {
  const { content: c } = useSiteNav();
  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="footer-brand">
          <img className="footer-wordmark" src={wordmarkWhite} alt="Vello" />
          <span className="footer-tagline">{c.footer.tagline}</span>
        </div>
        {c.footer.columns.map((col) => (
          <nav key={col.title} className="footer-col" aria-label={col.title}>
            <span className="footer-col-title">{col.title}</span>
            {col.links.map((link) => (
              <SiteLinkTo key={`${link.label}-${link.target}`} target={link.target} className="footer-link">
                {link.label}
              </SiteLinkTo>
            ))}
          </nav>
        ))}
      </div>
      <div className="footer-bottom">
        <span>{c.footer.copyright}</span>
        <a className="footer-action" href={deletion.href} onClick={(e) => deletion.onNavigate(e, deletion.href)}>{deletion.label}</a>
        <span>{c.footer.region}</span>
      </div>
    </footer>
  );
}

/** A served-market page inside the nav and footer. */
export function SiteFrame({ children, deletion }: { children: ReactNode; deletion: Parameters<typeof SiteFooter>[0]['deletion'] }): ReactElement {
  return (
    <div className="site">
      <SiteHeader />
      <main>{children}</main>
      <SiteFooter deletion={deletion} />
    </div>
  );
}
